import { z } from 'zod';

import { GroupDoc } from '@seller-area/contexts/commercial-group';

type DocType = keyof typeof GroupDoc;
type DocValue = string | null | undefined;

const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;
const CNPJ_BASE_LENGTH = 12;
const CNPJ_CHARACTER_OFFSET = 48;

const CPF_PATTERN = /^[0-9]{11}$/;
const CNPJ_PATTERN = /^[A-Z0-9]{12}[0-9]{2}$/;
const MASK_CHARACTERS = /[./-]/g;

const CPF_FIRST_WEIGHTS = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const CPF_SECOND_WEIGHTS = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_FIRST_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_SECOND_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const DOC_LENGTHS: Record<DocType, number> = { CPF: CPF_LENGTH, CNPJ: CNPJ_LENGTH };
const DOC_PLACEHOLDERS: Record<DocType, string> = { CPF: '000.000.000-00', CNPJ: '00.000.000/0000-00' };
const DOC_MASK_GROUPS: Record<DocType, Array<[number, string]>> = {
  CPF: [
    [3, '.'],
    [3, '.'],
    [3, '-'],
    [2, ''],
  ],
  CNPJ: [
    [2, '.'],
    [3, '.'],
    [3, '/'],
    [4, '-'],
    [2, ''],
  ],
};

export const generateDocNumberFormat = (docType: DocType) => {
  return {
    placeholder: DOC_PLACEHOLDERS[docType],
    format: docType === 'CNPJ' ? '##.###.###/####-##' : '###.###.###-##',
    mask: '_',
    allowNegative: false,
    fixedDecimalScale: false,
    decimalSeparator: undefined,
    thousandSeparator: undefined,
    decimalScale: 0,
    isNumericString: true,
  };
};

/** Digits only — safe to feed `NumericFormat` when `isNumericString` is true. */
export const digitsOnly = (value: string | null | undefined): string => String(value ?? '').replace(/\D/g, '');

const alphanumericOnly = (value: DocValue): string =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const stripMask = (value: DocValue): string =>
  String(value ?? '')
    .trim()
    .replace(MASK_CHARACTERS, '');

export const normalizeCpf = (value: DocValue): string => stripMask(value);

export const normalizeCnpj = (value: DocValue): string => stripMask(value).toUpperCase();

export const normalizeDocNumber = (docType: DocType, value: DocValue): string =>
  docType === 'CNPJ' ? normalizeCnpj(value) : normalizeCpf(value);

const checkDigit = (values: number[], weights: number[]): number => {
  const remainder = weights.reduce((sum, weight, index) => sum + values[index] * weight, 0) % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

const isSingleRepeatedCharacter = (value: string): boolean => new Set(value).size === 1;

export function isValidCpf(value: DocValue): boolean {
  const cpf = normalizeCpf(value);
  if (!CPF_PATTERN.test(cpf) || isSingleRepeatedCharacter(cpf)) return false;
  const digits = Array.from(cpf, Number);
  const first = checkDigit(digits, CPF_FIRST_WEIGHTS);
  const second = checkDigit([...digits.slice(0, CPF_FIRST_WEIGHTS.length), first], CPF_SECOND_WEIGHTS);
  return digits[9] === first && digits[10] === second;
}

export function isValidCnpj(value: DocValue): boolean {
  const cnpj = normalizeCnpj(value);
  if (!CNPJ_PATTERN.test(cnpj) || isSingleRepeatedCharacter(cnpj)) return false;
  const values = Array.from(cnpj, (character) => character.charCodeAt(0) - CNPJ_CHARACTER_OFFSET);
  const first = checkDigit(values, CNPJ_FIRST_WEIGHTS);
  const second = checkDigit([...values.slice(0, CNPJ_BASE_LENGTH), first], CNPJ_SECOND_WEIGHTS);
  return values[12] === first && values[13] === second;
}

export function isValidDocNumber(docType: DocType, value: DocValue): boolean {
  if (docType === 'CNPJ') return isValidCnpj(value);
  if (docType === 'CPF') return isValidCpf(value);
  return false;
}

export function sanitizeDocNumberInput(docType: DocType, value: DocValue): string {
  const characters = alphanumericOnly(value);
  if (docType !== 'CNPJ') return characters.replace(/\D/g, '').slice(0, CPF_LENGTH);
  return Array.from(characters).reduce((result, character) => {
    if (result.length >= CNPJ_LENGTH) return result;
    if (result.length >= CNPJ_BASE_LENGTH && !/[0-9]/.test(character)) return result;
    return result + character;
  }, '');
}

export function maskDocNumber(docType: DocType, value: DocValue): string {
  const characters = sanitizeDocNumberInput(docType, value);
  let masked = '';
  let position = 0;
  for (const [size, separator] of DOC_MASK_GROUPS[docType]) {
    const part = characters.slice(position, position + size);
    if (!part) break;
    position += part.length;
    masked += part;
    if (part.length === size && position < characters.length) masked += separator;
  }
  return masked;
}

export const docNumberInputProps = (docType: DocType) => ({
  firstPlaceholder: DOC_PLACEHOLDERS[docType],
  maskValue: (value: string) => maskDocNumber(docType, value),
  parseValue: (value: string) => sanitizeDocNumberInput(docType, value),
});

/**
 * Mask CPF/CNPJ from raw or already-masked input.
 * Incomplete values stay as digits so a second mask pass cannot double punctuation.
 */
export const formatDocNumber = (docType: DocType, value: string) => {
  if (docType === 'CNPJ') {
    const characters = alphanumericOnly(value);
    if (characters.length !== CNPJ_LENGTH) return characters;
    return maskDocNumber('CNPJ', characters);
  }
  const digits = digitsOnly(value);
  if (digits.length !== CPF_LENGTH) return digits;
  return maskDocNumber('CPF', digits);
};

export const docTypeOptions = [
  { label: 'CNPJ', value: 'CNPJ' },
  { label: 'CPF', value: 'CPF' },
];

export function isDocIncomplete(docType: DocType, docNumber: string | undefined): boolean {
  if (!docNumber) return true;
  if (docType === 'CNPJ') return alphanumericOnly(docNumber).length < CNPJ_LENGTH;
  if (docType === 'CPF') return digitsOnly(docNumber).length < CPF_LENGTH;
  return true;
}

export function docNumberIssue(docType: DocType, docNumber: DocValue): string | null {
  const normalized = normalizeDocNumber(docType, docNumber);
  if (normalized.length !== DOC_LENGTHS[docType]) {
    return docType === 'CNPJ' ? 'O CNPJ deve ter 14 caracteres.' : 'O CPF deve ter 11 dígitos.';
  }
  if (!isValidDocNumber(docType, normalized)) {
    return docType === 'CNPJ'
      ? 'CNPJ inválido. Confira os caracteres informados.'
      : 'CPF inválido. Confira os dígitos informados.';
  }
  return null;
}

export function refineDocNumber({ docType, docNumber }: { docType: DocType; docNumber: string }, ctx: z.RefinementCtx) {
  if (!docNumber) return;
  const message = docNumberIssue(docType, docNumber);
  if (!message) return;
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['docNumber'], message });
}

export const refineChangedDocNumber =
  (initialDocNumber: DocValue) => (data: { docType: DocType; docNumber: string }, ctx: z.RefinementCtx) => {
    if (initialDocNumber && alphanumericOnly(data.docNumber) === alphanumericOnly(initialDocNumber)) return;
    refineDocNumber(data, ctx);
  };
